import { createContext, useContext } from 'react'
import { ApiClient, initApiClient } from '@frontend/lib/request'

import { AnalyticsDashboard } from '@shared-types/analytics'
import {
  CompanyDepartment,
  Department,
  DepartmentAutomation,
  DepartmentAutomationEnablePayload,
  DepartmentAutomationUpdatePayload,
  DepartmentBlacklistedContactsUpdatePayload,
  DepartmentCreationPayload,
  DepartmentEnabledUpdatePayload,
  DepartmentsListFilterParam,
  DepartmentUpdatePayload,
  EnhancedDepartment,
  ServicePrice,
  ServicePriceCreationPayload,
} from '@shared-types/department'
import { BasicPagination, DeletedItem } from '@shared-types/domain'
import { Collection, Identifier, Nullable } from '@shared-types/util'

export interface FacilityDepartmentsClient {
  listDepartments: (
    companyId: Identifier,
    facilityId: Identifier,
    params?: DepartmentsListFilterParam
  ) => Promise<Collection<Department>>
  listAccessibleDepartments: (
    companyId: Identifier,
    params?: DepartmentsListFilterParam
  ) => Promise<Collection<CompanyDepartment>>
  listAllDepartments: (companyId: Identifier, facilityId: Identifier) => Promise<Department[]>
  getEnhancedDepartment: (
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier
  ) => Promise<EnhancedDepartment>
  getDepartmentAnalyticsDashboard: (
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier
  ) => Promise<{ url: Nullable<string> }>
  createDepartment: (
    companyId: Identifier,
    facilityId: Identifier,
    payload: DepartmentCreationPayload
  ) => Promise<Department>
  updateDepartment: (
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier,
    payload: DepartmentUpdatePayload
  ) => Promise<Department>
  updateDepartmentEnabled: (
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier,
    payload: DepartmentEnabledUpdatePayload
  ) => Promise<Department>
  getDepartmentAutomation: (
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier
  ) => Promise<DepartmentAutomation>
  enableDepartmentAutomation: (
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier,
    payload: DepartmentAutomationEnablePayload
  ) => Promise<DepartmentAutomation>
  updateDepartmentAutomation: (
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier,
    payload: DepartmentAutomationUpdatePayload
  ) => Promise<DepartmentAutomation>
  replaceDepartmentBlacklistedContacts: (
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier,
    payload: DepartmentBlacklistedContactsUpdatePayload
  ) => Promise<Department>
  listServicePrices: (
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier
  ) => Promise<ServicePrice[]>
  createServicePrice: (
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier,
    payload: ServicePriceCreationPayload
  ) => Promise<ServicePrice>
  deleteServicePrice: (
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier,
    serviceId: Identifier
  ) => Promise<DeletedItem>
}

class DefaultFacilityDepartmentsClient implements FacilityDepartmentsClient {
  client: ApiClient

  constructor() {
    this.client = initApiClient({})
  }

  async listDepartments(
    companyId: Identifier,
    facilityId: Identifier,
    params?: DepartmentsListFilterParam
  ): Promise<Collection<Department>> {
    return this.client
      .get(`/companies/${companyId}/facilities/${facilityId}/departments`, { params })
      .then((res) => res.data)
  }

  async listAccessibleDepartments(
    companyId: Identifier,
    params?: BasicPagination
  ): Promise<Collection<CompanyDepartment>> {
    return this.client
      .get(`/companies/${companyId}/accessible_departments`, { params })
      .then((res) => res.data)
  }

  async listAllDepartments(companyId: Identifier, facilityId: Identifier): Promise<Department[]> {
    return this.client
      .get(`/companies/${companyId}/facilities/${facilityId}/departments/_all`)
      .then((res) => res.data)
  }

  async getEnhancedDepartment(
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier
  ): Promise<EnhancedDepartment> {
    return this.client
      .get(`/companies/${companyId}/facilities/${facilityId}/departments/${departmentId}/details`)
      .then((res) => res.data)
  }

  async getDepartmentAnalyticsDashboard(
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier
  ): Promise<AnalyticsDashboard> {
    return this.client
      .get(
        `/companies/${companyId}/facilities/${facilityId}/departments/${departmentId}/analytics_dashboard`
      )
      .then((res) => res.data)
  }

  async createDepartment(
    companyId: Identifier,
    facilityId: Identifier,
    payload: DepartmentCreationPayload
  ): Promise<Department> {
    return this.client
      .post(`/companies/${companyId}/facilities/${facilityId}/departments`, payload)
      .then((res) => res.data)
  }

  async updateDepartment(
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier,
    payload: DepartmentUpdatePayload
  ): Promise<Department> {
    return this.client
      .patch(
        `/companies/${companyId}/facilities/${facilityId}/departments/${departmentId}`,
        payload
      )
      .then((res) => res.data)
  }

  async updateDepartmentEnabled(
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier,
    payload: DepartmentEnabledUpdatePayload
  ): Promise<Department> {
    return this.client
      .put(
        `/companies/${companyId}/facilities/${facilityId}/departments/${departmentId}/enabled`,
        payload
      )
      .then((res) => res.data)
  }

  async getDepartmentAutomation(
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier
  ): Promise<DepartmentAutomation> {
    return this.client
      .get(
        `/companies/${companyId}/facilities/${facilityId}/departments/${departmentId}/automation`
      )
      .then((res) => res.data)
  }

  async enableDepartmentAutomation(
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier,
    payload: DepartmentAutomationEnablePayload
  ): Promise<DepartmentAutomation> {
    return this.client
      .put(
        `/companies/${companyId}/facilities/${facilityId}/departments/${departmentId}/automation/enabled`,
        payload
      )
      .then((res) => res.data)
  }

  async updateDepartmentAutomation(
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier,
    payload: DepartmentAutomationUpdatePayload
  ): Promise<DepartmentAutomation> {
    return this.client
      .patch(
        `/companies/${companyId}/facilities/${facilityId}/departments/${departmentId}/automation`,
        payload
      )
      .then((res) => res.data)
  }

  async replaceDepartmentBlacklistedContacts(
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier,
    payload: DepartmentBlacklistedContactsUpdatePayload
  ): Promise<Department> {
    return this.client
      .put(
        `/companies/${companyId}/facilities/${facilityId}/departments/${departmentId}/blacklisted_contacts`,
        payload
      )
      .then((res) => res.data)
  }

  async listServicePrices(
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier
  ): Promise<ServicePrice[]> {
    return this.client
      .get(
        `/companies/${companyId}/facilities/${facilityId}/departments/${departmentId}/service_prices`
      )
      .then((res) => res.data)
  }

  async createServicePrice(
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier,
    payload: ServicePriceCreationPayload
  ): Promise<ServicePrice> {
    return this.client
      .post(
        `/companies/${companyId}/facilities/${facilityId}/departments/${departmentId}/service_prices`,
        payload
      )
      .then((res) => res.data)
  }

  async deleteServicePrice(
    companyId: Identifier,
    facilityId: Identifier,
    departmentId: Identifier,
    serviceId: Identifier
  ): Promise<DeletedItem> {
    return this.client
      .delete(
        `/companies/${companyId}/facilities/${facilityId}/departments/${departmentId}/service_prices/${serviceId}`
      )
      .then((res) => res.data)
  }
}

const FacilityDepartmentsClientContext = createContext<FacilityDepartmentsClient>(
  new DefaultFacilityDepartmentsClient()
)

export function useFacilityDepartmentsClient() {
  return useContext(FacilityDepartmentsClientContext)
}
